import React from 'react';
import { useParams } from 'react-router-dom';
import { getAppDisplayName } from '../constants/apps';
import './Privacy.css';

const LAST_UPDATED = 'September 14, 2026';

const Privacy = () => {
    const { appName } = useParams();
    const displayName = getAppDisplayName(appName);

    return (
        <div className="privacy">
            <div className="privacy-header">
                <h1>Privacy Policy for {displayName}</h1>
                <p className="last-updated"><strong>Last Updated:</strong> {LAST_UPDATED}</p>
            </div>
            <div className="privacy-content">
                <section>
                    <h2>Introduction</h2>
                    <p>{displayName} ("we," "our," or "the App") is an app for iPhone, iPad and Apple Watch for following US and Canadian stocks, ETFs and the markets around them. This Privacy Policy explains what information the App handles, where it is stored, and what is sent to the data sources it uses.</p>
                    <p>{displayName} tracks symbols, not holdings. It doesn't ask what you own or what you paid, and it has no accounts, logins or servers.</p>
                </section>

                <section>
                    <h2>Information the App Stores</h2>

                    <h3>Data You Provide</h3>
                    <ul>
                        <li><strong>Tracked symbols</strong>: The stocks and ETFs you add, with their names, country, type, currency and sector</li>
                        <li><strong>Watchlists</strong>: The named lists you group your stocks into</li>
                        <li><strong>Price alerts</strong>: The conditions you set for notifications</li>
                        <li><strong>Search history</strong>: Recent symbol and company searches</li>
                        <li><strong>Settings</strong>: Default currency, chart options, notification preferences, and which indices, currencies and cryptocurrencies to show</li>
                        <li><strong>FRED API key</strong>: If you choose to enter one</li>
                    </ul>

                    <h3>Data the App Downloads</h3>
                    <ul>
                        <li><strong>Market data</strong>: Prices and price history for stocks, ETFs, indices, commodities, sectors, currencies and cryptocurrencies</li>
                        <li><strong>Research data</strong>: Company details, earnings, analyst ratings, dividends, short interest, options, related stocks, news, SEC filings and insider trades</li>
                        <li><strong>Economic data</strong>: Interest rates, bond yields, inflation, employment and GDP figures</li>
                        <li><strong>Saved daily prices</strong>: Each stock's daily open, high, low, close and volume, saved so charts can be drawn without downloading them again</li>
                    </ul>

                    <h3>Data We Do NOT Collect</h3>
                    <ul>
                        <li>Personal identification information (name, email, address, phone number)</li>
                        <li>Account information (the App has no accounts or logins)</li>
                        <li>Your holdings, share counts or purchase prices</li>
                        <li>Location data</li>
                        <li>Device identifiers for tracking purposes</li>
                        <li>Analytics or usage tracking data</li>
                        <li>Advertising data</li>
                        <li>Payment or financial account information</li>
                    </ul>
                </section>

                <section>
                    <h2>Where Your Data Lives</h2>

                    <h3>On Your Device</h3>
                    <p>Everything you add is stored <strong>on your device</strong>. The developer operates no servers, and none of your data is ever sent to us.</p>

                    <h3>iCloud Sync</h3>
                    <p>If you're signed in to iCloud, your stocks, saved prices, watchlists, price alerts and search history sync between your iPhone and iPad through your <strong>private iCloud database</strong>, which only you and Apple can access. We cannot see this data. Without iCloud, the App keeps everything on the device.</p>
                    <p>iCloud is provided by Apple and is subject to <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noopener noreferrer">Apple's Privacy Policy</a>.</p>

                    <h3>Apple Watch</h3>
                    <p>The iPhone sends a small snapshot of your symbols, names, currencies and latest prices to your paired Apple Watch whenever prices refresh or your stock list changes. This transfer happens directly between your own devices.</p>

                    <h3>Notifications</h3>
                    <p>All notifications (price alerts, market open and close, earnings reminders, new 8-K filings and large insider buys) are created <strong>on your device</strong>. There is no notification server, and no push notification token is sent to us.</p>

                    <h3>Background Refresh</h3>
                    <p>While the App is closed, iOS periodically wakes it to refresh prices, check alerts and save closing prices. This work runs entirely on your device, using the same data sources described below.</p>
                </section>

                <section>
                    <h2>Third-Party Data Sources</h2>
                    <p>To show market, research and economic data, the App requests it directly from the following services:</p>
                    <ul>
                        <li><strong>Yahoo Finance</strong>: Prices and history, company details, earnings, analyst ratings, dividends, short interest, options, related stocks, news and symbol search (<a href="https://legal.yahoo.com/us/en/yahoo/privacy/index.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>)</li>
                        <li><strong>SEC EDGAR</strong>: US company filings and insider transactions</li>
                        <li><strong>Bank of Canada</strong>: Policy rate, treasury bill and Government of Canada bond yields, core inflation, USD/CAD</li>
                        <li><strong>Statistics Canada</strong>: Canadian inflation, jobs, wages, GDP, retail sales and housing starts</li>
                        <li><strong>U.S. Department of the Treasury</strong>: Treasury par yield curve and average interest rates on federal debt</li>
                        <li><strong>U.S. Bureau of Labor Statistics</strong>: US inflation, unemployment, payrolls and wages</li>
                        <li><strong>Federal Reserve Bank of New York</strong>: Effective federal funds rate</li>
                        <li><strong>FRED (Federal Reserve Bank of St. Louis)</strong>: US GDP, retail sales, housing starts and release dates, only if you enter your own API key</li>
                    </ul>

                    <h3>What These Services Receive</h3>
                    <ul>
                        <li>Stock symbols, search text and data requests only, <strong>never personal information</strong></li>
                        <li>Like any internet request, these services can see your device's IP address</li>
                        <li>If you enter a FRED API key, it is stored on your device and sent <strong>only to FRED</strong></li>
                    </ul>
                    <p>News articles open in your browser, where the publisher's own privacy practices apply.</p>
                    <p><strong>We do not control these services' data practices.</strong> Please refer to their privacy policies for information about how they handle requests.</p>

                    <h3>Data Sharing</h3>
                    <p><strong>We do not collect, share, sell, or transmit your personal data to anyone.</strong> The only information that leaves your devices is the data requests described above and, if you use it, your private iCloud sync.</p>
                </section>

                <section>
                    <h2>Data Security</h2>
                    <ul>
                        <li>Data on your device is protected by iOS and watchOS data protection</li>
                        <li>iCloud sync uses your private CloudKit database, encrypted by Apple in transit and at rest</li>
                        <li>Requests to data sources are made over encrypted HTTPS connections</li>
                        <li>No method of electronic storage is 100% secure, and you are responsible for maintaining the security of your device and Apple Account</li>
                    </ul>
                </section>

                <section>
                    <h2>Your Rights and Choices</h2>

                    <h3>Access and Control</h3>
                    <ul>
                        <li><strong>View your data</strong>: All data is accessible within the App</li>
                        <li><strong>Modify your data</strong>: Add, edit or delete stocks, watchlists and price alerts at any time</li>
                        <li><strong>Export your data</strong>: Use data import and export in Settings</li>
                        <li><strong>Notifications</strong>: Turn each notification type on or off in Settings, or in iOS Settings</li>
                        <li><strong>iCloud sync</strong>: Turn iCloud off for the App in iOS Settings to keep data on the device only</li>
                    </ul>

                    <h3>Data Deletion</h3>
                    <ul>
                        <li>Deleting the App removes its data from that device</li>
                        <li>If you use iCloud sync, delete the App's data from iCloud separately in iOS Settings under your Apple Account, iCloud, then Manage Storage</li>
                        <li>Deleting the Apple Watch app removes its snapshot from the watch</li>
                    </ul>
                </section>

                <section>
                    <h2>Children's Privacy</h2>
                    <p>{displayName} is not intended for children under the age of 13. We do not collect personal information from anyone, including children under 13.</p>
                </section>

                <section>
                    <h2>Not Investment Advice</h2>
                    <p>Quotes may be delayed. Data in {displayName} is provided for information only and is not investment advice.</p>
                </section>

                <section>
                    <h2>Changes to This Privacy Policy</h2>
                    <p>We may update this Privacy Policy from time to time. We will notify you of any changes by:</p>
                    <ul>
                        <li>Posting the new Privacy Policy on this page</li>
                        <li>Updating the "Last Updated" date at the top of this policy</li>
                    </ul>
                    <p>You are advised to review this Privacy Policy periodically for any changes.</p>
                </section>

                <section>
                    <h2>International Users</h2>
                    <p>If you are using the App from outside the United States or Canada, please note that:</p>
                    <ul>
                        <li>Your data is stored on your device and, if you use it, in your private iCloud database</li>
                        <li>Data requests go to services in the United States and Canada, which may be subject to different data protection laws</li>
                        <li>By using the App, you consent to the processing of data as described in this policy</li>
                    </ul>
                </section>

                <section>
                    <h2>California Privacy Rights</h2>
                    <p>If you are a California resident, you have the right to:</p>
                    <ul>
                        <li>Know what personal information is collected (we collect none)</li>
                        <li>Know whether your personal information is sold or disclosed and to whom</li>
                        <li>Access your personal information</li>
                        <li>Request deletion of your personal information</li>
                        <li>Opt-out of the sale of personal information (we do not sell personal information)</li>
                    </ul>
                </section>

                <section>
                    <h2>Contact Us</h2>
                    <p>If you have any questions about this Privacy Policy or our data practices, please contact us at:</p>
                    <p><strong>Email:</strong> <a href="mailto:zeta.cuter2h@icloud.com">zeta.cuter2h@icloud.com</a><br />
                    <strong>Website:</strong> <a href="https://donallison.ca">donallison.ca</a></p>
                </section>

                <section>
                    <h2>Consent</h2>
                    <p>By using {displayName}, you consent to this Privacy Policy and agree to its terms.</p>
                </section>

                <section className="summary">
                    <h2>Summary</h2>
                    <p><strong>In simple terms:</strong></p>
                    <ul>
                        <li>✅ No accounts, analytics, advertising or tracking</li>
                        <li>✅ The developer operates no servers and never receives your data</li>
                        <li>✅ Your data stays on your devices and in your private iCloud database</li>
                        <li>✅ Data sources receive only symbols, search text and data requests</li>
                        <li>✅ Your FRED API key is stored on your device and sent only to FRED</li>
                        <li>✅ All notifications are created on your device</li>
                    </ul>
                    <p><strong>Last Updated:</strong> {LAST_UPDATED}</p>
                </section>
            </div>
        </div>
    );
}

export default Privacy;
